import { useState, useEffect } from 'react';
import { Box, MenuItem, FormHelperText } from '@mui/material';
import {
  DialogContainer,
  DialogTitle,
  DialogBody,
  DialogActions,
  CustomSelect,
  SaveConfirmationDialog,
  CustomTextField,
} from '../../../common/components';

/**
 * Diálogo de adicionales refactorizado usando componentes del common
 */
const AdditionalDialog = ({ open, onClose, onSave, selectedItem, editMode }) => {
  const [formData, setFormData] = useState({
    name: '',
    amount: '',
    prorate: '',
    mandatory: '',
  });

  const [errors, setErrors] = useState({});
  const [saveAttempted, setSaveAttempted] = useState(false);

  // Estado para el diálogo de confirmación
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [confirmMessage, setConfirmMessage] = useState('');
  const [confirmTitle, setConfirmTitle] = useState('');

  // Actualizar formulario cuando cambia el item seleccionado
  useEffect(() => {
    if (open) {
      setFormData({
        name: selectedItem?.name || '',
        amount: selectedItem?.amount || '',
        prorate: selectedItem?.prorate !== undefined ? selectedItem.prorate.toString() : '',
        mandatory: selectedItem?.mandatory !== undefined ? selectedItem.mandatory.toString() : '',
      });
      setErrors({});
      setSaveAttempted(false);
    }
  }, [open, selectedItem]);

  const handleInputChange = (event) => {
    const { name, value } = event.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
    // Limpiar error del campo al escribir
    if (errors[name]) {
      setErrors((prev) => ({
        ...prev,
        [name]: false,
      }));
    }
  };

  const handleSelectChange = (event) => {
    const { name, value } = event.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
    // Limpiar error del campo al seleccionar
    if (errors[name]) {
      setErrors((prev) => ({
        ...prev,
        [name]: false,
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.name) {
      newErrors.name = true;
    }
    if (!formData.amount) {
      newErrors.amount = true;
    }
    if (formData.prorate === '') {
      newErrors.prorate = true;
    }
    if (formData.mandatory === '') {
      newErrors.mandatory = true;
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = () => {
    setSaveAttempted(true);

    if (!validateForm()) {
      return;
    }

    const isProrateEnabled = Number(formData.prorate) === 1;
    const isMandatoryEnabled = Number(formData.mandatory) === 1;

    if (isProrateEnabled || isMandatoryEnabled) {
      setConfirmTitle('Confirmar cambios');

      let message = '';

      if (isProrateEnabled && isMandatoryEnabled) {
        message =
          'El adicional se agregará a todos los contratos y el monto se actualizará en todos los adicionales de los contratos. ¿Desea continuar?';
      } else if (isProrateEnabled) {
        message =
          'El monto del adicional se actualizará en todos los adicionales de los contratos. ¿Desea continuar?';
      } else if (isMandatoryEnabled) {
        message =
          'El adicional se agregará automáticamente a todos los contratos. ¿Desea continuar?';
      }

      setConfirmMessage(message);
      setConfirmDialogOpen(true);
    } else {
      // Si no necesitamos confirmación, guardamos directamente
      confirmSave();
    }
  };

  const confirmSave = () => {
    setConfirmDialogOpen(false);
    onSave(formData);
  };

  const cancelSave = () => {
    setConfirmDialogOpen(false);
  };

  return (
    <>
      <DialogContainer open={open} onClose={onClose} size="small">
        <DialogTitle>
          {editMode ? `Editar Adicional: "${formData.name}"` : 'Agregar Adicional'}
        </DialogTitle>

        <DialogBody sx={{ display: 'block' }}>
          <Box>
            <Box sx={{ display: 'block' }}>
              <CustomTextField
                margin="dense"
                name="name"
                label="Nombre"
                type="text"
                fullWidth
                value={formData.name}
                onChange={handleInputChange}
                error={errors.name || (saveAttempted && !formData.name)}
              />
              {(errors.name || (saveAttempted && !formData.name)) && (
                <FormHelperText error>Debes ingresar un nombre.</FormHelperText>
              )}

              <CustomTextField
                margin="dense"
                name="amount"
                label="Monto ($)"
                type="number"
                fullWidth
                value={formData.amount}
                onChange={handleInputChange}
                error={errors.amount || (saveAttempted && !formData.amount)}
              />
              {(errors.amount || (saveAttempted && !formData.amount)) && (
                <FormHelperText error>Debes ingresar un monto.</FormHelperText>
              )}

              <CustomSelect
                label="Se prorratea?"
                labelId="prorate-label"
                name="prorate"
                value={formData.prorate}
                onChange={handleSelectChange}
                error={errors.prorate || (saveAttempted && formData.prorate === '')}
              >
                <MenuItem value="1">Si</MenuItem>
                <MenuItem value="0">No</MenuItem>
              </CustomSelect>
              {(errors.prorate || (saveAttempted && formData.prorate === '')) && (
                <FormHelperText error>Debes seleccionar si se prorratea.</FormHelperText>
              )}
              <CustomSelect
                label="Es obligatorio?"
                labelId="mandatory-label"
                name="mandatory"
                value={formData.mandatory}
                onChange={handleSelectChange}
                sx={{ mt: 2 }}
                error={errors.mandatory || (saveAttempted && formData.mandatory === '')}
              >
                <MenuItem value="1">Si</MenuItem>
                <MenuItem value="0">No</MenuItem>
              </CustomSelect>
              {(errors.mandatory || (saveAttempted && formData.mandatory === '')) && (
                <FormHelperText error>Debes seleccionar si es obligatorio.</FormHelperText>
              )}
            </Box>
          </Box>
        </DialogBody>
        <DialogActions handleClose={onClose} handleSave={handleSave} />
      </DialogContainer>

      {/* Diálogo de confirmación */}
      <SaveConfirmationDialog
        open={confirmDialogOpen}
        title={confirmTitle}
        message={confirmMessage}
        onConfirm={confirmSave}
        onClose={cancelSave}
      />
    </>
  );
};

export default AdditionalDialog;
